import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { X, MapPin } from 'lucide-react';

import { useApi } from '../../lib/api.js';
import { Button } from '../../components/ui/Button.jsx';

// =============================================================================
// ServiceAreasCard — cities a chauffeur / operator gets job offers for.
// Type a city + Enter to add (new cities become options for everyone).
// Backend: GET  /offers/meta/service-areas
//          POST /offers/driver/:driverId/service-areas  { areas: [...] }
// =============================================================================

function titleCase(s) {
    return String(s || '')
        .trim()
        .replace(/\s+/g, ' ')
        .toLowerCase()
        .replace(/(^|[\s-])\S/g, (m) => m.toUpperCase());
}

export function ServiceAreasCard({ driver }) {
    const api = useApi();
    const queryClient = useQueryClient();

    const saved = driver?.service_areas || [];
    const [areas, setAreas] = useState(saved);
    const [input, setInput] = useState('');

    useEffect(() => {
        setAreas(driver?.service_areas || []);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [driver?.id, (driver?.service_areas || []).join('|')]);

    const optionsQ = useQuery({
        queryKey: ['service-areas'],
        queryFn: () => api.get('/offers/meta/service-areas'),
    });
    const options = (optionsQ.data?.areas || []).filter((a) => !areas.includes(a));

    const dirty = areas.join('|') !== saved.join('|');

    function addArea(raw) {
        const name = titleCase(raw);
        if (!name) return;
        if (!areas.some((a) => a.toLowerCase() === name.toLowerCase())) {
            setAreas([...areas, name]);
        }
        setInput('');
    }

    const saveMutation = useMutation({
        mutationFn: () => api.post(`/offers/driver/${driver.id}/service-areas`, { areas }),
        onSuccess: () => {
            toast.success('Service areas saved');
            queryClient.invalidateQueries({ queryKey: ['service-areas'] });
            queryClient.invalidateQueries({
                predicate: (q) => Array.isArray(q.queryKey) && q.queryKey.includes(driver.id),
            });
        },
        onError: (err) => toast.error(err?.message || 'Could not save service areas'),
    });

    if (!driver?.id) return null;

    return (
                <div className="card">
            <div className="card-header">
                <h2 className="text-sm font-semibold text-ink flex items-center gap-2">
                    <MapPin className="h-4 w-4" />
                    Service areas
                </h2>
                <p className="text-xs text-ink-subtle mt-0.5">Cities this chauffeur receives job offers for.</p>
            </div>
            <div className="card-body">

            <div className="flex flex-wrap gap-2 mb-3">
                {areas.length === 0 && (
                    <span className="text-sm text-ink-subtle italic">No area set — will appear for every area.</span>
                )}
                {areas.map((a) => (
                    <span
                        key={a}
                        className="inline-flex items-center gap-1 px-2.5 py-1 text-sm rounded-md bg-ink text-paper"
                    >
                        {a}
                        <button
                            type="button"
                            onClick={() => setAreas(areas.filter((x) => x !== a))}
                            className="opacity-70 hover:opacity-100"
                            aria-label={`Remove ${a}`}
                        >
                            <X className="h-3 w-3" />
                        </button>
                    </span>
                ))}
            </div>

            <div className="flex gap-2">
                <input
                    type="text"
                    list={`service-area-options-${driver.id}`}
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            e.preventDefault();
                            addArea(input);
                        }
                    }}
                    placeholder="Type a city and press Enter"
                    maxLength={60}
                    className="flex-1 px-3 py-1.5 text-sm border border-paper-border rounded-md bg-paper text-ink"
                />
                <datalist id={`service-area-options-${driver.id}`}>
                    {options.map((a) => <option key={a} value={a} />)}
                </datalist>
                <Button type="button" variant="secondary" onClick={() => addArea(input)} disabled={!input.trim()}>
                    Add
                </Button>
            </div>

            {options.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-3">
                    {options.map((a) => (
                        <button
                            key={a}
                            type="button"
                            onClick={() => addArea(a)}
                            className="px-2 py-0.5 text-xs rounded border border-paper-border text-ink-subtle hover:text-ink"
                        >
                            + {a}
                        </button>
                    ))}
                </div>
            )}

            {dirty && (
                <div className="flex justify-end gap-2 mt-4">
                    <Button type="button" variant="secondary" onClick={() => setAreas(saved)} disabled={saveMutation.isPending}>
                        Cancel
                    </Button>
                    <Button type="button" variant="primary" onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending}>
                        {saveMutation.isPending ? 'Saving…' : 'Save areas'}
                    </Button>
                </div>
             )}
            </div>
        </div>
    );
}
